<script setup lang="ts">
/* ============================================================
   PasteModal — "Paste markdown" window from the + menu

   A small modal that pops up so you can paste/type markdown to start
   a new document. Submitting hands the text back to the reader, which
   opens it through the same path as a dropped file or clipboard paste.
   Esc closes; ⌘/Ctrl+Enter submits.
   ============================================================ */

import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'

const emit = defineEmits<{
  (e: 'submit', text: string): void
  (e: 'close'): void
}>()

const text = ref('')
const area = ref<HTMLTextAreaElement | null>(null)

function submit(): void {
  if (!text.value.trim()) return
  emit('submit', text.value)
}

function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    e.preventDefault()
    emit('close')
  }
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown, true)
  nextTick(() => area.value?.focus())
})
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown, true))
</script>

<template>
  <div class="paste-modal" role="dialog" aria-modal="true" aria-label="Paste markdown">
    <div class="paste-backdrop" @click="emit('close')"></div>
    <div class="paste-panel">
      <div class="paste-header">
        <h2 class="paste-title">Paste markdown</h2>
        <button class="btn btn-ghost btn-sm" aria-label="Close" @click="emit('close')">✕</button>
      </div>
      <textarea
        ref="area"
        v-model="text"
        class="paste-area"
        spellcheck="false"
        placeholder="Paste or type markdown here…"
        @keydown.meta.enter="submit"
        @keydown.ctrl.enter="submit"
      ></textarea>
      <div class="paste-actions">
        <span class="paste-hint">⌘/Ctrl + Enter to open</span>
        <span class="paste-actions-buttons">
          <button class="btn btn-default btn-md" @click="emit('close')">Cancel</button>
          <button class="btn btn-primary btn-md" :disabled="!text.trim()" @click="submit">
            Open document
          </button>
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.paste-modal {
  position: absolute;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-6);
}
.paste-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.32);
  backdrop-filter: blur(2px);
}
:global(:root[data-mode='dark']) .paste-backdrop {
  background: rgba(0, 0, 0, 0.55);
}
.paste-panel {
  position: relative;
  width: min(640px, 100%);
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg, 0 24px 48px rgba(0, 0, 0, 0.18));
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.paste-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--border);
}
.paste-title {
  font-size: var(--text-lg);
  font-weight: 600;
  margin: 0;
  color: var(--text);
}
.paste-area {
  width: 100%;
  min-height: 240px;
  resize: vertical;
  border: 0;
  background: var(--bg);
  color: var(--text);
  font-family: var(--mono-font);
  font-size: 13px;
  line-height: 1.55;
  padding: var(--space-4) var(--space-5);
}
.paste-area:focus {
  outline: none;
}
.paste-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-5);
  border-top: 1px solid var(--border);
}
.paste-hint {
  font-size: var(--text-sm);
  color: var(--text-faint);
}
.paste-actions-buttons {
  display: flex;
  gap: var(--space-2);
}
</style>
